import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import {
	Card,
	CardBody,
	CardHeader,
} from "../../../../_metronic/_partials/controls";
import ReplayIcon from "@material-ui/icons/Replay";
import LocalShippingIcon from "@material-ui/icons/LocalShipping";
import {
	createBoxInvite,
	getBoxInvites,
	resetBoxInvite,
	updateBoxInvite,
} from "../../../../api/box-invite";
import { alertError, alertSuccess } from "../../../../utils/logger";
import { shallowEqual, useSelector } from "react-redux";

/**
 * Panel de la campaña de las cajas fuerte.
 *
 * Una fila por caja, en el orden impreso. El estado es derivado por el API
 * (`stage`) a partir de las marcas de tiempo, así que aquí solo se pinta.
 */

const STAGES = [
	{ key: "pendiente_envio", label: "Pendiente de envío", bg: "#fee2e2", fg: "#991b1b" },
	{ key: "sobre_listo", label: "Sobre listo", bg: "#ffedd5", fg: "#9a3412" },
	{ key: "enviada", label: "Enviada", bg: "#f1f5f9", fg: "#475569" },
	{ key: "escaneada", label: "Escaneada", bg: "#e0f2fe", fg: "#075985" },
	{ key: "dinamica_iniciada", label: "Dinámica iniciada", bg: "#fef9c3", fg: "#854d0e" },
	{ key: "caja_abierta", label: "Caja abierta", bg: "#fde68a", fg: "#92400e" },
	{ key: "sesion_vista", label: "Sesión vista", bg: "#ddd6fe", fg: "#5b21b6" },
	{ key: "reservada", label: "Sesión pedida", bg: "#dcfce7", fg: "#166534" },
	{ key: "cancelada", label: "Cancelada", bg: "#e5e7eb", fg: "#374151", terminal: true },
];

const STAGE_BY_KEY = STAGES.reduce((acc, s) => ({ ...acc, [s.key]: s }), {});

const PERSONAS = [
	{ key: "delegada", label: "Delegada (Commercial Excellence)" },
	{ key: "medica", label: "Médica (Medical Education / Marketing)" },
];

const EMPTY_NEW = { name: "", company: "", persona: "medica", locale: "es" };

function formatDate(iso) {
	if (!iso) return "—";
	try {
		return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
	} catch (e) {
		return "—";
	}
}

function StageChip({ stage }) {
	const s = STAGE_BY_KEY[stage] || STAGES[0];
	return <Chip size="small" label={s.label} style={{ background: s.bg, color: s.fg, fontWeight: 600 }} />;
}

function Milestone({ label, value, count }) {
	return (
		<div style={{ minWidth: 150 }}>
			<Typography variant="caption" color="textSecondary">
				{label}
			</Typography>
			<Typography variant="body2" style={{ fontWeight: value ? 600 : 400 }}>
				{formatDate(value)}
				{count ? ` · ${count}×` : ""}
			</Typography>
		</div>
	);
}

export default function BoxInvitesPage() {
	const authUser = useSelector((s) => s.authentication?.user, shallowEqual);
	const isAdmin = String(authUser?.role || "").toLowerCase() === "admin";
	const [loading, setLoading] = useState(false);
	const [rows, setRows] = useState([]);
	const [stageFilter, setStageFilter] = useState("");
	const [query, setQuery] = useState("");
	const [detail, setDetail] = useState(null);
	const [notes, setNotes] = useState("");
	const [active, setActive] = useState(true);
	const [persona, setPersona] = useState("delegada");
	const [saving, setSaving] = useState(false);
	const [creating, setCreating] = useState(false);
	const [resetting, setResetting] = useState(false);
	const [shippingId, setShippingId] = useState("");
	const [newBox, setNewBox] = useState(null);

	const load = useCallback(async () => {
		if (!isAdmin) return;
		setLoading(true);
		try {
			const res = await getBoxInvites();
			setRows(res.status === 200 && Array.isArray(res.data) ? res.data : []);
		} catch (error) {
			setRows([]);
			alertError({ error, customMessage: "No se pudieron cargar las cajas." });
		} finally {
			setLoading(false);
		}
	}, [isAdmin]);

	useEffect(() => {
		load();
	}, [load]);

	const openDetail = (row) => {
		setDetail(row);
		setNotes(row.notes || "");
		setActive(row.active !== false);
		setPersona(row.persona || "delegada");
	};

	const closeDetail = () => setDetail(null);

	const save = async () => {
		if (!detail?._id || saving) return;
		setSaving(true);
		try {
			const res = await updateBoxInvite(detail._id, { notes, active, persona });
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(detail._id) ? res.data : r)));
				setDetail(res.data);
				alertSuccess("Caja actualizada.");
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo guardar." });
		} finally {
			setSaving(false);
		}
	};

	/** Cambia el estado postal (sobre listo / enviada); el API pone las fechas. */
	const setPostal = async (row, patch) => {
		if (!row?._id || shippingId) return;
		setShippingId(String(row._id));
		try {
			const res = await updateBoxInvite(row._id, patch);
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(row._id) ? res.data : r)));
				if (detail && String(detail._id) === String(row._id)) setDetail(res.data);
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo actualizar el envío." });
		} finally {
			setShippingId("");
		}
	};

	const setShipped = (row, shipped) => setPostal(row, { shipped });
	const setPrepared = (row, prepared) => setPostal(row, { prepared });

	const reset = async () => {
		if (!detail?._id || resetting) return;
		const ok = window.confirm(
			`Reiniciar la caja ${detail.boxNumber} (${detail.name}): se borran escaneos, respuestas y reserva. ¿Seguir?`
		);
		if (!ok) return;
		setResetting(true);
		try {
			const res = await resetBoxInvite(detail._id);
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(detail._id) ? res.data : r)));
				setDetail(res.data);
				alertSuccess("Caja reiniciada: vuelve a estar como recién enviada.");
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo reiniciar la caja." });
		} finally {
			setResetting(false);
		}
	};

	const create = async () => {
		if (!newBox || creating) return;
		const name = newBox.name.trim();
		const company = newBox.company.trim();
		if (!name || !company) {
			alertError({ customMessage: "Nombre y empresa son obligatorios." });
			return;
		}
		setCreating(true);
		try {
			const res = await createBoxInvite({ ...newBox, name, company });
			if (res.status === 201 && res.data) {
				setRows((prev) => [...prev, res.data].sort((a, b) => a.boxNumber - b.boxNumber));
				setNewBox(null);
				alertSuccess(`Caja ${res.data.boxNumber} creada con clave ${res.data.code}.`);
				openDetail(res.data);
			}
		} catch (error) {
			alertError({ error, customMessage: error?.response?.data?.message || "No se pudo crear la caja." });
		} finally {
			setCreating(false);
		}
	};

	const filtered = useMemo(() => {
		const q = query.trim().toLowerCase();
		return rows.filter((r) => {
			if (stageFilter && r.stage !== stageFilter) return false;
			if (!q) return true;
			return [r.name, r.company, r.code, String(r.boxNumber)]
				.some((v) => String(v || "").toLowerCase().includes(q));
		});
	}, [rows, stageFilter, query]);

	// Resumen de embudo: cuántas cajas han llegado al menos a cada etapa.
	// Las canceladas quedan fuera del embudo y se cuentan aparte.
	const funnel = useMemo(() => {
		const live = rows.filter((r) => r.stage !== "cancelada");
		const order = STAGES.filter((s) => !s.terminal).map((s) => s.key);
		return STAGES.map((s) => ({
			...s,
			total: s.terminal ? rows.length : live.length,
			count: s.terminal
				? rows.filter((r) => r.stage === s.key).length
				: live.filter((r) => order.indexOf(r.stage) >= order.indexOf(s.key)).length,
		}));
	}, [rows]);

	/** Cancela (con motivo) o reactiva la caja de la ficha. */
	const setCancelled = async (cancelled) => {
		if (!detail?._id || saving) return;
		let cancelReason = "";
		if (cancelled) {
			cancelReason = window.prompt(
				`Cancelar la caja ${detail.boxNumber} (${detail.name}). Motivo (opcional):`,
				detail.cancelReason || ""
			);
			if (cancelReason === null) return;
		}
		setSaving(true);
		try {
			const res = await updateBoxInvite(detail._id, { cancelled, cancelReason });
			if (res.status === 200 && res.data) {
				setRows((prev) => prev.map((r) => (String(r._id) === String(detail._id) ? res.data : r)));
				setDetail(res.data);
				alertSuccess(cancelled ? "Caja cancelada: ya no abre." : "Caja reactivada.");
			}
		} catch (error) {
			alertError({ error, customMessage: "No se pudo cambiar la cancelación." });
		} finally {
			setSaving(false);
		}
	};

	if (!isAdmin) {
		return (
			<Card>
				<CardHeader title="Cajas fuerte" />
				<CardBody>
					<Typography color="textSecondary">Solo los administradores pueden ver esta sección.</Typography>
				</CardBody>
			</Card>
		);
	}

	return (
		<>
			<Card>
				<CardHeader title="Cajas fuerte · campaña farma" />
				<CardBody>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginBottom: 20 }}>
						{funnel.map((s) => (
							<Paper
								key={s.key}
								variant="outlined"
								style={{ padding: "8px 14px", background: s.bg, color: s.fg, minWidth: 120 }}
							>
								<Typography variant="caption" style={{ display: "block", fontWeight: 600 }}>
									{s.label}
								</Typography>
								<Typography variant="h6" style={{ fontWeight: 800, lineHeight: 1.2 }}>
									{s.count}
									<span style={{ fontSize: 12, fontWeight: 400 }}> / {s.total}</span>
								</Typography>
							</Paper>
						))}
					</div>

					<div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", marginBottom: 20 }}>
						<Button variant="outlined" size="small" onClick={load} disabled={loading}>
							Actualizar
						</Button>
						<Button
							variant="contained"
							color="primary"
							size="small"
							startIcon={<AddIcon />}
							onClick={() => setNewBox({ ...EMPTY_NEW })}
						>
							Nueva caja
						</Button>
						<FormControl variant="outlined" size="small" style={{ minWidth: 190 }}>
							<InputLabel id="box-stage-filter-label">Estado</InputLabel>
							<Select
								labelId="box-stage-filter-label"
								label="Estado"
								value={stageFilter}
								onChange={(e) => setStageFilter(e.target.value)}
							>
								<MenuItem value="">Todos</MenuItem>
								{STAGES.map((s) => (
									<MenuItem key={s.key} value={s.key}>
										{s.label}
									</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							size="small"
							variant="outlined"
							label="Buscar nombre, empresa o clave"
							value={query}
							onChange={(e) => setQuery(e.target.value)}
							style={{ minWidth: 260 }}
						/>
					</div>

					{loading ? (
						<div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
							<CircularProgress />
						</div>
					) : (
						<TableContainer component={Paper} variant="outlined">
							<Table size="small">
								<TableHead>
									<TableRow>
										<TableCell>#</TableCell>
										<TableCell>Destinatario</TableCell>
										<TableCell>Clave</TableCell>
										<TableCell>Personaje</TableCell>
										<TableCell>Estado</TableCell>
										<TableCell>Enviada</TableCell>
										<TableCell>Escaneada</TableCell>
										<TableCell>Abierta</TableCell>
										<TableCell>Sesión pedida</TableCell>
										<TableCell align="right">Acciones</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{filtered.length === 0 ? (
										<TableRow>
											<TableCell colSpan={10} align="center">
												No hay cajas con estos filtros.
											</TableCell>
										</TableRow>
									) : (
										filtered.map((row) => (
											<TableRow
												key={row._id}
												style={{ opacity: row.active === false || row.stage === "cancelada" ? 0.5 : 1 }}
											>
												<TableCell>{row.boxNumber}</TableCell>
												<TableCell>
													<div style={{ fontWeight: 600 }}>{row.name || "—"}</div>
													<div style={{ fontSize: 12, color: "#64748b" }}>{row.company || "—"}</div>
												</TableCell>
												<TableCell>
													<code>{row.code}</code>
												</TableCell>
												<TableCell>
													{row.persona === "medica" ? "Médica" : "Delegada"}
													{row.round > 1 ? (
														<span style={{ fontSize: 12, color: "#64748b" }}> · tanda {row.round}</span>
													) : null}
												</TableCell>
												<TableCell>
													<StageChip stage={row.stage} />
													{row.active === false ? (
														<Chip size="small" label="Desactivada" style={{ marginLeft: 6 }} />
													) : null}
												</TableCell>
												<TableCell>
													{row.stage === "cancelada" ? (
														<span style={{ color: "#64748b" }}>—</span>
													) : row.shippedAt ? (
														<>
															{formatDate(row.shippedAt)}
															<Button
																size="small"
																style={{ marginLeft: 6, minWidth: 0, color: "#64748b" }}
																disabled={Boolean(shippingId)}
																onClick={() => {
																	if (window.confirm(`Dejar la caja ${row.boxNumber} como pendiente de envío?`)) {
																		setShipped(row, false);
																	}
																}}
															>
																Deshacer
															</Button>
														</>
													) : row.preparedAt ? (
														<>
															<Button
																size="small"
																variant="outlined"
																startIcon={
																	shippingId === String(row._id) ? (
																		<CircularProgress size={14} />
																	) : (
																		<LocalShippingIcon fontSize="small" />
																	)
																}
																disabled={Boolean(shippingId)}
																onClick={() => setShipped(row, true)}
															>
																Marcar enviada
															</Button>
															<Button
																size="small"
																style={{ marginLeft: 6, minWidth: 0, color: "#64748b" }}
																disabled={Boolean(shippingId)}
																onClick={() => setPrepared(row, false)}
															>
																Deshacer
															</Button>
														</>
													) : (
														<Button
															size="small"
															variant="outlined"
															disabled={Boolean(shippingId)}
															onClick={() => setPrepared(row, true)}
														>
															Sobre listo
														</Button>
													)}
												</TableCell>
												<TableCell>{formatDate(row.firstScanAt)}</TableCell>
												<TableCell>{formatDate(row.openedAt)}</TableCell>
												<TableCell>{formatDate(row.bookingRequestedAt)}</TableCell>
												<TableCell align="right">
													<Button size="small" onClick={() => openDetail(row)}>
														Ver
													</Button>
												</TableCell>
											</TableRow>
										))
									)}
								</TableBody>
							</Table>
						</TableContainer>
					)}
				</CardBody>
			</Card>

			<Dialog open={Boolean(detail)} onClose={closeDetail} maxWidth="md" fullWidth>
				<DialogTitle>
					Caja {detail?.boxNumber} · {detail?.name || "—"}
				</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" gutterBottom>
						{detail?.company} · clave <code>{detail?.code}</code> · <StageChip stage={detail?.stage} />
					</Typography>
					{detail?.cancelledAt ? (
						<Paper
							variant="outlined"
							style={{ padding: "10px 14px", margin: "8px 0 4px", background: "#f3f4f6" }}
						>
							<Typography variant="body2">
								<strong>Cancelada el {formatDate(detail.cancelledAt)}.</strong>{" "}
								{detail.cancelReason || "Sin motivo indicado."} La clave ya no abre la caja.
							</Typography>
						</Paper>
					) : null}

					<Typography variant="subtitle2" style={{ fontWeight: 800, margin: "16px 0 8px" }}>
						Recorrido
					</Typography>
					<div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 20 }}>
						<Milestone label="Sobre listo" value={detail?.preparedAt} />
						<Milestone label="Sobre enviado" value={detail?.shippedAt} />
						<Milestone label="Primer escaneo" value={detail?.firstScanAt} count={detail?.scanCount} />
						<Milestone label="Último escaneo" value={detail?.lastScanAt} />
						<Milestone label="Caja abierta (531)" value={detail?.openedAt} />
						<Milestone label="Página de sesión" value={detail?.sessionViewedAt} count={detail?.sessionViewCount} />
						<Milestone label="Sesión pedida" value={detail?.bookingRequestedAt} />
					</div>

					{detail?.bookingEmail ? (
						<Typography variant="body2" style={{ marginBottom: 20 }}>
							Email para la sesión: <strong>{detail.bookingEmail}</strong>
						</Typography>
					) : null}

					<Typography variant="subtitle2" style={{ fontWeight: 800, marginBottom: 8 }}>
						Respuestas de la dinámica
					</Typography>
					<Paper variant="outlined" style={{ padding: "12px 14px", marginBottom: 20 }}>
						{(detail?.answers || []).length === 0 ? (
							<Typography variant="body2" color="textSecondary">
								Todavía no ha respondido ningún paso.
							</Typography>
						) : (
							[...(detail?.answers || [])]
								.sort((a, b) => a.step - b.step)
								.map((a) => (
									<Typography key={a.step} variant="body2" style={{ marginBottom: 4 }}>
										<strong>Paso {a.step}:</strong> {a.value || "(sin respuesta)"}{" "}
										<span style={{ color: "#64748b", fontSize: 12 }}>· {formatDate(a.answeredAt)}</span>
									</Typography>
								))
						)}
					</Paper>

					<Divider style={{ margin: "8px 0 20px" }} />

					<TextField
						fullWidth
						multiline
						minRows={3}
						label="Notas internas"
						variant="outlined"
						size="small"
						value={notes}
						onChange={(e) => setNotes(e.target.value.slice(0, 2000))}
						disabled={saving}
						style={{ marginBottom: 12 }}
					/>
					<FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
						<InputLabel id="box-persona-label">Personaje del vídeo</InputLabel>
						<Select
							labelId="box-persona-label"
							label="Personaje del vídeo"
							value={persona}
							onChange={(e) => setPersona(e.target.value)}
							disabled={saving}
						>
							{PERSONAS.map((p) => (
								<MenuItem key={p.key} value={p.key}>
									{p.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControlLabel
						control={
							<Switch
								checked={Boolean(detail?.preparedAt)}
								onChange={(e) => setPrepared(detail, e.target.checked)}
								disabled={Boolean(shippingId) || saving || Boolean(detail?.shippedAt)}
							/>
						}
						label={detail?.preparedAt ? `Sobre listo el ${formatDate(detail.preparedAt)}` : "Sobre por preparar"}
					/>
					<br />
					<FormControlLabel
						control={
							<Switch
								checked={Boolean(detail?.shippedAt)}
								onChange={(e) => setShipped(detail, e.target.checked)}
								disabled={Boolean(shippingId) || saving}
							/>
						}
						label={detail?.shippedAt ? `Sobre enviado el ${formatDate(detail.shippedAt)}` : "Sobre pendiente de envío"}
					/>
					<br />
					<FormControlLabel
						control={<Switch checked={active} onChange={(e) => setActive(e.target.checked)} disabled={saving} />}
						label="Clave activa (si se desactiva, la caja deja de abrir)"
					/>

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 24 }}>
						{detail?.cancelledAt ? (
							<Button style={{ color: "#166534" }} disabled={saving} onClick={() => setCancelled(false)}>
								Reactivar caja
							</Button>
						) : (
							<Button style={{ color: "#991b1b" }} disabled={saving} onClick={() => setCancelled(true)}>
								Cancelar caja
							</Button>
						)}
						<Button
							style={{ marginRight: "auto", color: "#b45309" }}
							startIcon={resetting ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />}
							disabled={resetting || saving || ["pendiente_envio", "sobre_listo", "enviada"].includes(detail?.stage)}
							onClick={reset}
						>
							Reiniciar dinámica
						</Button>
						<Button onClick={closeDetail} disabled={saving}>
							Cerrar
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
							disabled={saving}
							onClick={save}
						>
							Guardar
						</Button>
					</div>
				</DialogContent>
			</Dialog>

			<Dialog open={Boolean(newBox)} onClose={() => (creating ? null : setNewBox(null))} maxWidth="sm" fullWidth>
				<DialogTitle>Nueva caja</DialogTitle>
				<DialogContent>
					<Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
						La clave y el número de caja se generan al guardar. Son los que hay que mandar a imprenta:
						no se pueden cambiar después.
					</Typography>
					<TextField
						fullWidth
						label="Nombre completo"
						variant="outlined"
						size="small"
						value={newBox?.name || ""}
						onChange={(e) => setNewBox((p) => ({ ...p, name: e.target.value }))}
						disabled={creating}
						style={{ marginBottom: 12 }}
					/>
					<TextField
						fullWidth
						label="Empresa"
						variant="outlined"
						size="small"
						value={newBox?.company || ""}
						onChange={(e) => setNewBox((p) => ({ ...p, company: e.target.value }))}
						disabled={creating}
						style={{ marginBottom: 12 }}
					/>
					<FormControl variant="outlined" size="small" fullWidth style={{ marginBottom: 12 }}>
						<InputLabel id="new-box-persona-label">Personaje del vídeo</InputLabel>
						<Select
							labelId="new-box-persona-label"
							label="Personaje del vídeo"
							value={newBox?.persona || "medica"}
							onChange={(e) => setNewBox((p) => ({ ...p, persona: e.target.value }))}
							disabled={creating}
						>
							{PERSONAS.map((p) => (
								<MenuItem key={p.key} value={p.key}>
									{p.label}
								</MenuItem>
							))}
						</Select>
					</FormControl>
					<FormControl variant="outlined" size="small" fullWidth>
						<InputLabel id="new-box-locale-label">Idioma</InputLabel>
						<Select
							labelId="new-box-locale-label"
							label="Idioma"
							value={newBox?.locale || "es"}
							onChange={(e) => setNewBox((p) => ({ ...p, locale: e.target.value }))}
							disabled={creating}
						>
							<MenuItem value="es">Castellano</MenuItem>
							<MenuItem value="ca">Catalán</MenuItem>
							<MenuItem value="en">Inglés</MenuItem>
						</Select>
					</FormControl>

					<div style={{ display: "flex", justifyContent: "flex-end", gap: 8, margin: "24px 0 12px" }}>
						<Button onClick={() => setNewBox(null)} disabled={creating}>
							Cancelar
						</Button>
						<Button
							variant="contained"
							color="primary"
							startIcon={creating ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
							disabled={creating}
							onClick={create}
						>
							Crear caja
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	);
}
